<template>
  <HrPageLayout
    title="Organization Structure"
    subtitle="Define units, managers, and who reports within each unit"
    icon="🏛️"
    :breadcrumb-items="[]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-secondary" :disabled="loadingUnits" @click="fetchOrgUnits">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loadingUnits }"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="openNewUnit">
        <i class="bx bx-plus"></i> New unit
      </button>
    </template>

    <div class="org-wrap">
      <aside class="org-panel org-units">
        <div class="org-panel-head">
          <div>
            <h3>Units</h3>
            <p>{{ flatUnits.length }} unit{{ flatUnits.length === 1 ? '' : 's' }}</p>
          </div>
        </div>

        <div v-if="loadingUnits" class="org-empty">
          <div class="hr-spinner"></div>
          <p>Loading organisation structure…</p>
        </div>

        <div v-else-if="!flatUnits.length" class="org-empty">
          <i class="bx bx-git-branch"></i>
          <p>No units yet. Create one to get started.</p>
          <button type="button" class="hr-btn hr-btn-primary hr-btn-sm" @click="openNewUnit">Create first unit</button>
        </div>

        <div v-else class="org-unit-list">
          <button
            v-for="unit in flatUnits"
            :key="unit.id"
            type="button"
            class="org-unit-item"
            :class="{ active: selectedUnitId === unit.id }"
            :style="{ paddingLeft: (14 + unit.level * 18) + 'px' }"
            @click="selectUnit(unit.id)"
          >
            <span class="org-unit-rail" v-if="unit.level > 0"></span>
            <div class="org-unit-main">
              <div class="org-unit-top">
                <strong>{{ unit.name }}</strong>
                <span class="org-chip">{{ unit.membersCount || 0 }}</span>
              </div>
              <div class="org-unit-meta">
                <span>{{ unit.code || 'No code' }}</span>
                <span>·</span>
                <span>{{ unit.managerName || 'Manager not set' }}</span>
              </div>
            </div>
          </button>
        </div>
      </aside>

      <section class="org-main">
        <div class="org-panel">
          <div class="org-panel-head">
            <div>
              <h3>{{ unitForm.id ? 'Edit unit' : 'Create unit' }}</h3>
              <p>{{ unitForm.id ? 'Update unit details and hierarchy' : 'Add a new unit to the structure' }}</p>
            </div>
          </div>

          <div class="org-form-grid">
            <label class="org-field">
              <span>Name <em>*</em></span>
              <input v-model="unitForm.name" type="text" class="hr-input" placeholder="e.g. Finance Department">
            </label>
            <label class="org-field">
              <span>Code</span>
              <input v-model="unitForm.code" type="text" class="hr-input" placeholder="e.g. FIN">
            </label>
            <label class="org-field">
              <span>Parent unit</span>
              <select v-model="unitForm.parentId" class="hr-select">
                <option value="">— None (top level) —</option>
                <option v-for="unit in flatUnits" :key="'parent-' + unit.id" :value="unit.id">
                  {{ '—'.repeat(unit.level) }} {{ unit.name }}
                </option>
              </select>
            </label>
            <label class="org-field">
              <span>Manager (Emp No / ID / Email)</span>
              <input v-model="unitForm.managerIdentifier" type="text" class="hr-input" placeholder="Enter identifier">
            </label>
            <label class="org-field org-field-full">
              <span>Description</span>
              <textarea v-model="unitForm.description" rows="2" class="hr-input" placeholder="Optional description"></textarea>
            </label>
          </div>

          <div class="org-form-actions">
            <button type="button" class="hr-btn hr-btn-outline-secondary" @click="resetUnitForm">
              <i class="bx bx-reset"></i> Reset
            </button>
            <div class="org-form-actions-right">
              <button
                v-if="unitForm.id"
                type="button"
                class="hr-btn hr-btn-danger"
                :disabled="savingUnit || deletingUnit"
                @click="deleteUnit"
              >
                <span v-if="deletingUnit" class="hr-spinner small"></span>
                Delete
              </button>
              <button type="button" class="hr-btn hr-btn-primary" :disabled="savingUnit" @click="saveUnit">
                <span v-if="savingUnit" class="hr-spinner small"></span>
                {{ unitForm.id ? 'Save changes' : 'Save unit' }}
              </button>
            </div>
          </div>
        </div>

        <div class="org-panel">
          <div class="org-panel-head">
            <div>
              <h3>Unit members</h3>
              <p v-if="selectedUnit">{{ selectedUnit.name }} · {{ currentMembers.length }} member{{ currentMembers.length === 1 ? '' : 's' }}</p>
              <p v-else>Select a unit to manage its members</p>
            </div>
            <button
              v-if="selectedUnitId"
              type="button"
              class="hr-btn hr-btn-outline-primary hr-btn-sm"
              @click="toggleMemberForm = !toggleMemberForm"
            >
              <i class="bx" :class="toggleMemberForm ? 'bx-minus' : 'bx-plus'"></i>
              {{ toggleMemberForm ? 'Hide form' : 'Add member' }}
            </button>
          </div>

          <div v-if="!selectedUnitId" class="org-empty soft">
            <i class="bx bx-user-plus"></i>
            <p>Select a unit on the left to view and assign members.</p>
          </div>

          <template v-else>
            <div v-if="toggleMemberForm" class="org-member-form">
              <div class="org-form-grid">
                <label class="org-field">
                  <span>Employee <em>*</em></span>
                  <select v-model="memberForm.identifier" class="hr-select" :disabled="loadingEmployees">
                    <option value="">Select employee</option>
                    <option
                      v-for="employee in employeeOptions"
                      :key="'employee-' + employee.id"
                      :value="employee.empNo || employee.nationalId"
                    >
                      {{ employeeOptionLabel(employee) }}
                    </option>
                  </select>
                </label>
                <label class="org-field">
                  <span>Role</span>
                  <input v-model="memberForm.role" type="text" class="hr-input" placeholder="e.g. Associate">
                </label>
                <label class="org-field org-field-full">
                  <span>Supervisor</span>
                  <select v-model="memberForm.reportsTo" class="hr-select" :disabled="loadingEmployees || memberForm.setAsManager">
                    <option value="">Use unit manager</option>
                    <option
                      v-for="supervisor in supervisorOptions"
                      :key="'supervisor-' + supervisor.id"
                      :value="supervisor.empNo || supervisor.nationalId"
                    >
                      {{ employeeOptionLabel(supervisor) }}
                    </option>
                  </select>
                  <small>This reporting line controls leave approval for the employee.</small>
                </label>
              </div>
              <div class="org-checks">
                <label class="org-check">
                  <input v-model="memberForm.isPrimary" type="checkbox">
                  Primary assignment
                </label>
                <label class="org-check">
                  <input v-model="memberForm.setAsManager" type="checkbox">
                  Set as manager
                </label>
              </div>
              <div class="org-form-actions-right">
                <button type="button" class="hr-btn hr-btn-success" :disabled="addingMember" @click="assignMember">
                  <span v-if="addingMember" class="hr-spinner small"></span>
                  Assign member
                </button>
              </div>
            </div>

            <div v-if="!currentMembers.length" class="org-empty soft">
              <i class="bx bx-group"></i>
              <p>No members assigned to this unit yet.</p>
            </div>

            <div v-else class="org-table-wrap">
              <table class="org-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Role</th>
                    <th>Supervisor</th>
                    <th>Primary</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="member in currentMembers" :key="member.id">
                    <td>
                      <strong>{{ member.employeeName || member.empNo }}</strong>
                      <div class="org-sub">{{ member.positionTitle || '—' }}</div>
                    </td>
                    <td>{{ member.role || '—' }}</td>
                    <td>{{ member.supervisorName || member.supervisorEmpNo || 'Unit manager / unassigned' }}</td>
                    <td>
                      <span class="org-badge" :class="member.is_primary ? 'yes' : 'no'">
                        {{ member.is_primary ? 'Yes' : 'No' }}
                      </span>
                    </td>
                    <td class="text-end">
                      <button
                        type="button"
                        class="hr-btn hr-btn-outline-danger hr-btn-sm"
                        :disabled="removingMember"
                        @click="removeMember(member)"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </div>
      </section>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "OrgStructureManagement",
  components: { HrPageLayout },
  data() {
    return {
      loadingUnits: false,
      loadingEmployees: false,
      savingUnit: false,
      deletingUnit: false,
      addingMember: false,
      removingMember: false,
      toggleMemberForm: false,
      flatUnits: [],
      treeUnits: [],
      selectedUnitId: null,
      currentMembers: [],
      employeeOptions: [],
      unitForm: {
        id: null,
        name: "",
        code: "",
        parentId: "",
        managerIdentifier: "",
        description: ""
      },
      memberForm: {
        identifier: "",
        role: "",
        isPrimary: false,
        setAsManager: false,
        reportsTo: ""
      }
    };
  },
  mounted() {
    this.fetchSupervisorOptions();
    this.fetchOrgUnits();
  },
  computed: {
    selectedUnit() {
      return this.flatUnits.find((u) => String(u.id) === String(this.selectedUnitId)) || null;
    },
    supervisorOptions() {
      const selected = String(this.memberForm.identifier || "").trim().toUpperCase();
      return this.employeeOptions.filter((employee) => {
        const empNo = String(employee.empNo || "").trim().toUpperCase();
        const nationalId = String(employee.nationalId || "").trim().toUpperCase();
        return !selected || (empNo !== selected && nationalId !== selected);
      });
    }
  },
  methods: {
    employeeOptionLabel(employee) {
      const name = String(employee.employeeName || "").trim() ||
        [employee.firstName, employee.middleName, employee.lastName].filter(Boolean).join(" ").trim();
      const id = employee.empNo || employee.nationalId || "";
      const department = employee.departmentName ? ` · ${employee.departmentName}` : "";
      return `${name || id} (${id})${department}`;
    },
    async fetchSupervisorOptions() {
      this.loadingEmployees = true;
      try {
        const data = new FormData();
        data.append("function", "getSupervisorAssignmentOptions");
        const res = await execute(data, authUrl);
        if (!res?.data?.success) {
          throw new Error(res?.data?.message || "Failed to load employees");
        }
        this.employeeOptions = Array.isArray(res.data.data?.employees) ? res.data.data.employees : [];
      } catch (error) {
        this.employeeOptions = [];
        this.$toast?.error(error.message || "Failed to load employees") ||
          console.error("Error loading supervisor options:", error);
      } finally {
        this.loadingEmployees = false;
      }
    },
    async fetchOrgUnits() {
      this.loadingUnits = true;
      try {
        const data = new FormData();
        data.append("function", "getOrgUnits");
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.treeUnits = res.data.data.tree || [];
          this.flatUnits = res.data.data.flat || [];
          if (!this.selectedUnitId && this.flatUnits.length > 0) {
            this.selectUnit(this.flatUnits[0].id);
          } else if (this.selectedUnitId) {
            this.selectUnit(this.selectedUnitId);
          }
        } else {
          this.treeUnits = [];
          this.flatUnits = [];
          this.selectedUnitId = null;
          this.currentMembers = [];
          this.$toast?.error(res?.data?.message || "Failed to load organisation units") ||
            console.error(res?.data?.message || "Failed to load organisation units");
        }
      } catch (error) {
        this.treeUnits = [];
        this.flatUnits = [];
        this.selectedUnitId = null;
        this.currentMembers = [];
        this.$toast?.error("Error: " + error.message) ||
          console.error("Error loading organisation units:", error);
      } finally {
        this.loadingUnits = false;
      }
    },
    findUnitInTree(id, units = null) {
      const list = units || this.treeUnits;
      for (const unit of list) {
        if (unit.id === id) {
          return unit;
        }
        if (unit.children && unit.children.length) {
          const found = this.findUnitInTree(id, unit.children);
          if (found) return found;
        }
      }
      return null;
    },
    selectUnit(id) {
      this.selectedUnitId = id;
      const unit = this.findUnitInTree(id);
      if (!unit) {
        this.currentMembers = [];
        this.resetUnitForm();
        return;
      }
      this.unitForm = {
        id: unit.id,
        name: unit.name,
        code: unit.code || "",
        parentId: unit.parent_id || "",
        managerIdentifier: unit.manager_emp_no || "",
        description: unit.description || ""
      };
      this.currentMembers = unit.members || [];
    },
    openNewUnit() {
      this.resetUnitForm();
      this.selectedUnitId = null;
      this.currentMembers = [];
      this.toggleMemberForm = false;
    },
    resetUnitForm() {
      this.unitForm = {
        id: null,
        name: "",
        code: "",
        parentId: "",
        managerIdentifier: "",
        description: ""
      };
    },
    async saveUnit() {
      if (!this.unitForm.name) {
        this.$toast?.error("Name is required") || alert("Name is required");
        return;
      }
      this.savingUnit = true;
      try {
        const data = new FormData();
        data.append("function", "saveOrgUnit");
        if (this.unitForm.id) data.append("unitId", this.unitForm.id);
        data.append("name", this.unitForm.name);
        data.append("code", this.unitForm.code || "");
        data.append("parentId", this.unitForm.parentId || "");
        data.append("description", this.unitForm.description || "");
        data.append("managerIdentifier", this.unitForm.managerIdentifier || "");

        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Unit saved") || alert("Unit saved");
          await this.fetchOrgUnits();
        } else {
          throw new Error(res?.data?.message || "Failed to save unit");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.savingUnit = false;
      }
    },
    async deleteUnit() {
      if (!this.unitForm.id) return;
      if (!confirm("Are you sure you want to delete this unit?")) return;
      this.deletingUnit = true;
      try {
        const data = new FormData();
        data.append("function", "deleteOrgUnit");
        data.append("unitId", this.unitForm.id);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Unit deleted") || alert("Unit deleted");
          this.openNewUnit();
          await this.fetchOrgUnits();
        } else {
          throw new Error(res?.data?.message || "Failed to delete unit");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.deletingUnit = false;
      }
    },
    async assignMember() {
      if (!this.selectedUnitId) {
        this.$toast?.error("Select a unit first") || alert("Select a unit first");
        return;
      }
      if (!this.memberForm.identifier) {
        this.$toast?.error("Employee identifier is required") || alert("Employee identifier is required");
        return;
      }
      if (!this.memberForm.setAsManager && !this.memberForm.reportsTo) {
        const selectedUnit = this.findUnitInTree(this.selectedUnitId);
        if (!selectedUnit?.manager_national_id && !selectedUnit?.manager_emp_no) {
          this.$toast?.error("Select a supervisor or configure the unit manager first") ||
            alert("Select a supervisor or configure the unit manager first");
          return;
        }
      }
      this.addingMember = true;
      try {
        const data = new FormData();
        data.append("function", "assignOrgUnitMember");
        data.append("orgUnitId", this.selectedUnitId);
        data.append("employeeIdentifier", this.memberForm.identifier);
        data.append("role", this.memberForm.role || "");
        data.append("isPrimary", this.memberForm.isPrimary);
        data.append("setAsManager", this.memberForm.setAsManager);
        data.append("reportsToIdentifier", this.memberForm.reportsTo || "");

        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Member assigned") || alert("Member assigned");
          this.memberForm = {
            identifier: "",
            role: "",
            isPrimary: false,
            setAsManager: false,
            reportsTo: ""
          };
          await this.fetchOrgUnits();
        } else {
          throw new Error(res?.data?.message || "Failed to assign member");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.addingMember = false;
      }
    },
    async removeMember(member) {
      if (!member || !member.id) return;
      if (!confirm("Remove this member from the unit?")) return;
      this.removingMember = true;
      try {
        const data = new FormData();
        data.append("function", "removeOrgUnitMember");
        data.append("memberId", member.id);
        const res = await execute(data, authUrl);
        if (res && res.data && res.data.success) {
          this.$toast?.success(res.data.message || "Member removed") || alert("Member removed");
          await this.fetchOrgUnits();
        } else {
          throw new Error(res?.data?.message || "Failed to remove member");
        }
      } catch (error) {
        this.$toast?.error("Error: " + error.message) || alert("Error: " + error.message);
      } finally {
        this.removingMember = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.org-wrap {
  display: grid;
  grid-template-columns: minmax(280px, 340px) 1fr;
  gap: 16px;
  align-items: start;
}

.org-panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  overflow: hidden;
}

.org-units {
  position: sticky;
  top: 12px;
}

.org-main {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.org-panel-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(180deg, #f8fafc 0%, #fff 100%);
}

.org-panel-head h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.org-panel-head p {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.org-unit-list {
  max-height: 70vh;
  overflow-y: auto;
}

.org-unit-item {
  position: relative;
  width: 100%;
  display: block;
  border: 0;
  border-bottom: 1px solid #f1f5f9;
  background: transparent;
  text-align: left;
  padding: 12px 14px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.org-unit-item:hover {
  background: #f8fafc;
}

.org-unit-item.active {
  background: #ecfdf5;
  box-shadow: inset 3px 0 0 #059669;
}

.org-unit-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.org-unit-top strong {
  color: #0f172a;
  font-size: 14px;
}

.org-unit-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}

.org-chip {
  background: #f1f5f9;
  color: #334155;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
}

.org-unit-item.active .org-chip {
  background: #d1fae5;
  color: #047857;
}

.org-empty {
  padding: 36px 20px;
  text-align: center;
  color: #64748b;
}

.org-empty.soft {
  padding: 28px 16px;
}

.org-empty i {
  font-size: 36px;
  color: #94a3b8;
  display: block;
  margin-bottom: 8px;
}

.org-empty p {
  margin: 0 0 12px;
}

.org-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  padding: 18px;
}

.org-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}

.org-field em {
  color: #dc2626;
  font-style: normal;
}

.org-field-full {
  grid-column: 1 / -1;
}

.org-field small {
  font-weight: 500;
  color: #64748b;
}

.hr-input,
.hr-select {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  color: #0f172a;
  background: #fff;
}

.org-form-actions {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 0 18px 18px;
  flex-wrap: wrap;
}

.org-form-actions-right {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.org-member-form {
  margin: 0 18px 16px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.org-member-form .org-form-grid {
  padding: 0;
}

.org-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin: 10px 0 14px;
}

.org-check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #334155;
  font-weight: 500;
}

.org-table-wrap {
  overflow: auto;
  padding: 0 0 8px;
}

.org-table {
  width: 100%;
  border-collapse: collapse;
}

.org-table th,
.org-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  font-size: 13px;
  vertical-align: top;
}

.org-table th {
  background: #f8fafc;
  color: #64748b;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.org-sub {
  color: #94a3b8;
  font-size: 12px;
  margin-top: 2px;
}

.org-badge {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
}

.org-badge.yes {
  background: #d1fae5;
  color: #047857;
}

.org-badge.no {
  background: #f1f5f9;
  color: #64748b;
}

.text-end {
  text-align: right;
}

@media (max-width: 992px) {
  .org-wrap {
    grid-template-columns: 1fr;
  }
  .org-units {
    position: static;
  }
  .org-unit-list {
    max-height: 320px;
  }
}

@media (max-width: 640px) {
  .org-form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
